/**
 * Designer Standards — one global library (`settings/designerStandards`) of SOPs,
 * .2d / .3d templates and the timber-list export, managed from Settings → Project
 * Defaults and shown read-only in the "Designer Standards" folder on every project.
 *
 * Live library (not a per-project snapshot): updating a file here updates every project.
 */

import { db, storage } from './firebase';
import { ensureCrmStorageAccessBeforeUpload } from './crmStorageAccess';
import { doc, onSnapshot, runTransaction, Timestamp } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { DesignerStandardItem, DesignerStandardSubfolder } from '../types';
import { DESIGNER_STANDARD_SUBFOLDERS } from './projectFileFolders';

const DOC_PATH = ['settings', 'designerStandards'] as const;
const STORAGE_PREFIX = 'designer-standards';

function docRef() {
  return doc(db, DOC_PATH[0], DOC_PATH[1]);
}

function isSubfolder(raw: unknown): raw is DesignerStandardSubfolder {
  return DESIGNER_STANDARD_SUBFOLDERS.some(s => s.id === raw);
}

function itemFromRaw(raw: unknown): DesignerStandardItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const id = String(d.id ?? '').trim();
  const url = String(d.url ?? '').trim();
  if (!id || !url || !isSubfolder(d.subfolder)) return null;
  const uploadedAt = (d.uploadedAt as { toDate?: () => Date } | undefined)?.toDate?.();
  return {
    id,
    name: String(d.name ?? '') || 'Untitled',
    url,
    storagePath: String(d.storagePath ?? ''),
    mimeType: String(d.mimeType ?? '') || 'application/octet-stream',
    size: typeof d.size === 'number' ? d.size : 0,
    subfolder: d.subfolder,
    uploadedAt: uploadedAt instanceof Date ? uploadedAt : new Date(0),
    uploadedBy: String(d.uploadedBy ?? ''),
    uploadedByName: String(d.uploadedByName ?? ''),
    order: typeof d.order === 'number' ? d.order : 0,
  };
}

function itemToRaw(item: DesignerStandardItem): Record<string, unknown> {
  return {
    id: item.id,
    name: item.name,
    url: item.url,
    storagePath: item.storagePath,
    mimeType: item.mimeType,
    size: item.size,
    subfolder: item.subfolder,
    uploadedAt: Timestamp.fromDate(item.uploadedAt),
    uploadedBy: item.uploadedBy,
    uploadedByName: item.uploadedByName,
    order: item.order,
  };
}

/** Stable sort: subfolder order, then explicit `order`, then name. */
export function sortDesignerStandardItems(items: DesignerStandardItem[]): DesignerStandardItem[] {
  const subIndex = new Map(DESIGNER_STANDARD_SUBFOLDERS.map((s, i) => [s.id, i]));
  return [...items].sort((a, b) => {
    const sa = subIndex.get(a.subfolder) ?? 99;
    const sb = subIndex.get(b.subfolder) ?? 99;
    if (sa !== sb) return sa - sb;
    if (a.order !== b.order) return a.order - b.order;
    return a.name.localeCompare(b.name);
  });
}

export function parseDesignerStandardsDoc(data: Record<string, unknown> | undefined): DesignerStandardItem[] {
  const raw = Array.isArray(data?.items) ? (data!.items as unknown[]) : [];
  return sortDesignerStandardItems(raw.map(itemFromRaw).filter((x): x is DesignerStandardItem => !!x));
}

export function subscribeDesignerStandards(
  onData: (items: DesignerStandardItem[]) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    docRef(),
    snap => onData(parseDesignerStandardsDoc(snap.exists() ? snap.data() : undefined)),
    err => onError?.(err),
  );
}

function newItemId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function mutateItems(
  update: (items: DesignerStandardItem[]) => DesignerStandardItem[],
): Promise<DesignerStandardItem[]> {
  return runTransaction(db, async tx => {
    const snap = await tx.get(docRef());
    const current = parseDesignerStandardsDoc(snap.exists() ? snap.data() : undefined);
    const next = sortDesignerStandardItems(update(current));
    tx.set(
      docRef(),
      { items: next.map(itemToRaw), updatedAt: Timestamp.now() },
      { merge: true },
    );
    return next;
  });
}

export async function uploadDesignerStandard(
  file: File,
  subfolder: DesignerStandardSubfolder,
  uploadedBy: string,
  uploadedByName: string,
): Promise<DesignerStandardItem> {
  if (!isSubfolder(subfolder)) throw new Error('Unknown Designer Standards folder');
  const id = newItemId();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storagePath = `${STORAGE_PREFIX}/${id}/${safeName}`;
  await ensureCrmStorageAccessBeforeUpload();
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
  const url = await getDownloadURL(storageRef);

  const item: DesignerStandardItem = {
    id,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    subfolder,
    uploadedAt: new Date(),
    uploadedBy,
    uploadedByName,
    order: 0,
  };
  await mutateItems(items => {
    const siblings = items.filter(i => i.subfolder === subfolder);
    const maxOrder = siblings.reduce((m, i) => Math.max(m, i.order), -1);
    return [...items, { ...item, order: maxOrder + 1 }];
  });
  return item;
}

export async function renameDesignerStandard(id: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Name is required');
  await mutateItems(items => items.map(i => (i.id === id ? { ...i, name: trimmed } : i)));
}

export async function moveDesignerStandard(id: string, subfolder: DesignerStandardSubfolder): Promise<void> {
  if (!isSubfolder(subfolder)) throw new Error('Unknown Designer Standards folder');
  await mutateItems(items => {
    const siblings = items.filter(i => i.subfolder === subfolder && i.id !== id);
    const maxOrder = siblings.reduce((m, i) => Math.max(m, i.order), -1);
    return items.map(i => (i.id === id ? { ...i, subfolder, order: maxOrder + 1 } : i));
  });
}

/** Reorder within a subfolder; `orderedIds` is the full id order for that subfolder. */
export async function reorderDesignerStandards(
  subfolder: DesignerStandardSubfolder,
  orderedIds: string[],
): Promise<void> {
  const index = new Map(orderedIds.map((id, i) => [id, i]));
  await mutateItems(items =>
    items.map(i =>
      i.subfolder === subfolder && index.has(i.id) ? { ...i, order: index.get(i.id)! } : i,
    ),
  );
}

export async function deleteDesignerStandard(id: string): Promise<void> {
  let removed: DesignerStandardItem | undefined;
  await mutateItems(items => {
    removed = items.find(i => i.id === id);
    return items.filter(i => i.id !== id);
  });
  if (removed?.storagePath) {
    try {
      await deleteObject(ref(storage, removed.storagePath));
    } catch (err) {
      // Doc already updated; a missing blob is not fatal.
      console.warn('[designerStandards] blob delete failed', err);
    }
  }
}

/** Items for one subfolder, already sorted. */
export function designerStandardsInSubfolder(
  items: DesignerStandardItem[],
  subfolder: DesignerStandardSubfolder,
): DesignerStandardItem[] {
  return items.filter(i => i.subfolder === subfolder);
}
